/**
 * Execution-group helpers for the strategy modals.
 *
 * STBT/BTST strategies can have two distinct firings alive on the same view
 * day: yesterday's run (closing today) and today's run (just entered). Each
 * has its own `execution_id`. The strategy modals group rows by that id so
 * each run renders as one cohesive section instead of legs from different
 * runs interleaving by fill time.
 *
 * Helpers here are shape-agnostic — they accept a tiny structural type so
 * both `ScheduleRow` (Pro modal) and `PositionRow` (Classic modal) feed
 * through the same code.
 */

/** Minimal shape needed for grouping/labelling. */
export interface ExecutionGroupable {
  executionId?: string | null;
  executionDate?: string | null;
  closedDate?: string | null;
  pnl?: number | null;
  source?: 'entered' | 'closed' | 'carried' | null;
}

export interface ExecutionGroup<T extends ExecutionGroupable> {
  executionId: string;
  executionDate: string | null;
  closedDate: string | null;
  source: 'entered' | 'closed' | 'carried' | null;
  rows: T[];
  pnl: number;
}

/**
 * Bucket rows by `executionId`. Rows missing an id collapse into a single
 * synthetic "" bucket so the modal still renders them.
 *
 * Group ordering: latest `executionDate` first, so today's fresh run lands
 * above yesterday's closing run. Within a group the caller is responsible
 * for leg-level sorting (`compareLegRows` / `compareLegPositions`).
 */
export function groupRowsByExecution<T extends ExecutionGroupable>(rows: T[]): ExecutionGroup<T>[] {
  const map = new Map<string, ExecutionGroup<T>>();
  for (const r of rows) {
    const id = r.executionId || '';
    let g = map.get(id);
    if (!g) {
      g = {
        executionId: id,
        executionDate: r.executionDate ?? null,
        closedDate: r.closedDate ?? null,
        source: r.source ?? null,
        rows: [],
        pnl: 0,
      };
      map.set(id, g);
    }
    g.rows.push(r);
    g.pnl += r.pnl ?? 0;
    // Group date fields are pinned to the first row that carries them; sibling
    // rows of the same execution share the same execution/closed dates so this
    // is safe. `source` falls back to whichever row asserts it first.
    if (!g.executionDate && r.executionDate) g.executionDate = r.executionDate;
    if (!g.closedDate && r.closedDate) g.closedDate = r.closedDate;
    if (!g.source && r.source) g.source = r.source;
  }
  return Array.from(map.values()).sort(compareGroups);
}

function compareGroups<T extends ExecutionGroupable>(a: ExecutionGroup<T>, b: ExecutionGroup<T>): number {
  // Latest entry date first; null dates sink to the bottom.
  const aDate = a.executionDate || '';
  const bDate = b.executionDate || '';
  if (aDate && bDate) return bDate.localeCompare(aDate);
  if (aDate !== bDate) return aDate ? -1 : 1;
  return a.executionId.localeCompare(b.executionId);
}

/**
 * Pretty label for a group header.
 *
 * Same date as viewedDate → "today" word; other dates → "Tue 14" style. The
 * "entered/exiting" framing depends on whether this run entered on the view
 * day or earlier:
 *
 *   today's run:        "Entered today · Exits Wed 16"
 *   yesterday's run:    "Entered Tue 14 · Exiting today"
 *   carry (untouched):  "Entered Mon 13"
 *   closed days ago:    "Entered Mon 13 · Closed Wed 15"
 */
export function describeExecutionGroup(
  group: { executionDate: string | null; closedDate: string | null; source: ExecutionGroupable['source'] },
  viewedDate: string,
  scheduledExitDate: string | null,
): string {
  const entered = group.executionDate
    ? group.executionDate === viewedDate
      ? 'Entered today'
      : `Entered ${formatShortDate(group.executionDate)}`
    : null;

  let tail: string | null = null;
  if (group.closedDate) {
    tail = group.closedDate === viewedDate
      ? 'Exiting today'
      : `Closed ${formatShortDate(group.closedDate)}`;
  } else if (scheduledExitDate && scheduledExitDate !== group.executionDate) {
    tail = scheduledExitDate === viewedDate
      ? 'Exiting today'
      : `Exits ${formatShortDate(scheduledExitDate)}`;
  }

  if (entered && tail) return `${entered} · ${tail}`;
  return entered || tail || 'Execution';
}

/** "2026-05-17" → "Sat 17" (IST). Falls back to the raw string on parse error. */
export function formatShortDate(iso: string): string {
  // Anchor to noon UTC so the IST calendar day matches the ISO date string
  // regardless of timezone offset jitter.
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'Asia/Kolkata' });
  const day = d.toLocaleDateString('en-US', { day: 'numeric', timeZone: 'Asia/Kolkata' });
  return `${weekday} ${day}`;
}

/** Short hash for power users / log correlation: `exec_abc123…` → `exec_abc123`. */
export function shortExecutionId(id: string): string {
  if (!id) return '';
  if (id.length <= 12) return id;
  return `${id.slice(0, 12)}…`;
}
